import BigNumber from "bignumber.js";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import styled from "styled-components";
import Swal from "sweetalert2";
import { useProducts } from "../../client/product/useProduct";
import Header from "../../components/Header";
import OrderSummary from "../../components/OrderSummary";
import Spinner from "../../components/Spinner";
import { Heading4 } from "../../components/Typography";
import { COLOR_WHITE } from "../../constants/colors";
import { useAppContext } from "../../contexts/App";
import bgGray from "../../images/bg-gray.png";
import { CartItem } from "../../types/Product";
import CartEmpty from "./CartEmpty";
import Product from "./Product";

const Background = styled.div`
  min-height: 100vh;
  background-image: url(${bgGray});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  ${Heading4}
  color: ${COLOR_WHITE};
  text-align: center;
  margin-bottom: 100px;
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${COLOR_WHITE};
  margin: 50px 0px;
  @media (max-width: 768px) {
    margin: 25px 0px;
  }
`;

function Cart() {
  const appContext = useAppContext();
  const storedProducts = localStorage.getItem("cart");
  const parsedStoredProducts: CartItem[] = JSON.parse(storedProducts || "[]");

  const [productIds, setProductIds] = useState<string[]>([]);
  const { data: products, isLoading, isFetching } = useProducts(1, 1000, productIds.join(","));
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const totalProductPrice = cartItems.reduce((acc, cur) => {
    const accTmp = new BigNumber(acc);
    const curPriceTmp = new BigNumber(cur?.price || 0);
    const curCountTmp = new BigNumber(cur.count);
    return accTmp.plus(curPriceTmp.times(curCountTmp)).toNumber();
  }, 0);
  const [totalShippingCost, setTotalShippingCost] = useState(0);

  const totalPrice = new BigNumber(totalProductPrice).plus(totalShippingCost).toNumber();

  useEffect(() => {
    const totalShipping = cartItems.reduce((acc: any, product: any) => {
      const accTemp = new BigNumber(acc);
      return accTemp.plus(product.shipping ||0).toNumber();
    }, 0);
    setTotalShippingCost(totalShipping);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  useEffect(() => {
    const productIdsTemp = parsedStoredProducts.map((item) => item.id);
    setProductIds(productIdsTemp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!(products && products.data.length > 0)) return;
    const cartItemsTemp: CartItem[] = [];
    parsedStoredProducts.forEach((cartItem) => {
      const product = products.data.find((item) => item._id === cartItem.id);
      if (!product) {
        return;
      }
      cartItemsTemp.push({
        id: cartItem.id,
        name: product.name,
        image: product.images[0],
        price: product.price,
        count: cartItem.count,
        option: cartItem.option,
        storedAt: cartItem.storedAt,
        shipping: product.shipping,
      });
    });
    setCartItems(cartItemsTemp);
    localStorage.setItem("cart", JSON.stringify(cartItemsTemp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products?.data]);

  const handleRemove = (storedAt: number) => {
    const newCartItems = cartItems.filter((item) => item.storedAt !== storedAt);
    localStorage.setItem("cart", JSON.stringify(newCartItems));
    appContext?.setCartItemCount(newCartItems.length);
    setCartItems(newCartItems);
  };

  const handleQtyChange = (storedAt: number, count: number) => {
    let productId = "";
    let productOption = "";
    const newCartItems = cartItems.map((item) => {
      if (item.storedAt === storedAt) {
        productId = item.id;
        productOption = item.option;
        return { ...item, count };
      }
      return item;
    });

    const product = products?.data.find((item) => item._id === productId);
    const sku = product?.skus.find((item) => item.option === productOption);

    if (sku && count > sku.quantity) {
      Swal.fire({
        icon: "error",
        title: "Product out of stock",
        text: `You can't add more than ${sku.quantity} items`,
        showConfirmButton: false,
        timer: 1300,
        timerProgressBar: true,
      });
      return;
    }
    localStorage.setItem("cart", JSON.stringify(newCartItems));
    setCartItems(newCartItems);
  };

  const handlePlaceOrder = () => {
    window.location.href = "/payment";
  };

  return (
    <Background>
      <Header />
      <StyledContainer>
        {(isLoading || isFetching) && <Spinner animation="border" />}

        {!isLoading && !isFetching && cartItems.length === 0 && <CartEmpty />}

        {!isLoading && !isFetching && cartItems.length > 0 && (
          <>
            <Title>You have {cartItems.length} item in your cart</Title>
            <Row>
              <Col xl={7} className="mb-xl-0 mb-5">
                {cartItems.map((cartItem, index) => {
                  return (
                    <div key={cartItem.storedAt}>
                      <Product
                        image={cartItem.image || ""}
                        name={cartItem.name || ""}
                        description={`${cartItem.option.toUpperCase()}`}
                        price={cartItem.price || 0}
                        qty={cartItem.count}
                        storedAt={cartItem.storedAt}
                        setQty={handleQtyChange}
                        removeProduct={handleRemove}
                      />
                      {index !== cartItems.length - 1 && <Divider />}
                    </div>
                  );
                })}
              </Col>
              <Col xl={5}>
                <OrderSummary
                  size="sm"
                  title="Product Details"
                  orders={[
                    { label: `Total of ${cartItems.length} Products`, totalPrice: totalProductPrice },
                    { label: "Shipping fee", totalPrice: totalShippingCost },
                  ]}
                  handlePlaceOrder={handlePlaceOrder}
                  totalPrice={totalPrice}
                />
              </Col>
            </Row>
          </>
        )}
      </StyledContainer>
    </Background>
  );
}

export default Cart;
